const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/eCheckin/components/fields/AddressBlock.tsx';
import * as React from 'react';
import { forwardRef, useEffect } from 'react';
import _ from 'lodash';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import textMapCommon from 'App/config/common/translations';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import { CountryField } from './metakat/CountryField';
import {
    DropdownFieldConfig,
    DropdownFieldDefaultConfig,
    InputFieldConfig,
    TextFieldConfig,
    TextFieldDefaultConfig,
} from '../../types';
import { createFieldPath } from 'App/components/eCheckin/utils';
import { CIS4mNumberField } from '@cistec/common_web/modules/CIS4m/components/CIS4mNumberField';
import { actionSetCIS4mDataObject } from '@cistec/common_web/modules/CIS4m/actions';
import { useDispatch } from 'react-redux';
import { useCIS4mContext } from '@cistec/common_web/modules/CIS4m/context';

export interface AddressFieldsConfig {
    streetAndNumber?: TextFieldConfig;
    additionalInfo?: TextFieldConfig;
    postalCode?: TextFieldConfig;
    location?: TextFieldConfig;
    country?: DropdownFieldConfig;
}

export interface AddressFieldsDefaultConfig {
    streetAndNumber: TextFieldDefaultConfig;
    additionalInfo: TextFieldDefaultConfig;
    postalCode: TextFieldDefaultConfig;
    location: TextFieldDefaultConfig;
    country: DropdownFieldDefaultConfig;
}

export const defaultAddressFieldsConfig: AddressFieldsDefaultConfig = {
    streetAndNumber: {
        path: 'streetAndNumber',
        required: true,
        included: true,
        readonly: false,
    },
    additionalInfo: {
        path: 'adresszusatz',
        required: false,
        included: true,
        readonly: false,
    },
    postalCode: {
        path: 'plz',
        required: true,
        included: true,
        readonly: false,
    },
    location: {
        path: 'ort',
        required: true,
        included: true,
        readonly: false,
    },
    country: {
        path: 'country',
        required: true,
        included: true,
        readonly: false,
    },
};

interface Props {
    basePath: string;
    hidden?: boolean;
    required?: boolean;
    readonly?: boolean;
    fieldsConfig?: AddressFieldsConfig;
}

export const AddressBlock = forwardRef((props: Props, ref: any) => {
    const { basePath } = props;
    const formData = useCIS4mData();
    const { formId } = useCIS4mContext();
    const dispatch = useDispatch();
    const config = _.merge({}, defaultAddressFieldsConfig, props.fieldsConfig);

    const { streetAndNumber, additionalInfo, location, postalCode, country } = config;

    const PATH_STREET = createFieldPath(basePath, streetAndNumber.path);
    const PATH_ZUSATZ = createFieldPath(basePath, additionalInfo.path);
    const PATH_PLZ = createFieldPath(basePath, postalCode.path);
    const PATH_ORT = createFieldPath(basePath, location.path);
    const PATH_COUNTRY = createFieldPath(basePath, country.path);

    const mobileFormBlockRef = useCIS4m(ref);

    const hidden = props.hidden ?? false;
    const required = props.required ?? false;
    const readonly = props.readonly ?? false;

    const isReadonly = (fieldConfig: InputFieldConfig) => {
        return readonly || fieldConfig.readonly;
    };
    const isRequired = (fieldConfig: InputFieldConfig) => {
        return !isReadonly(fieldConfig) && required && fieldConfig.required;
    };

    const alphanumericPlzCountries = ['GB', 'CA', 'NL', 'IE', 'AR', 'BR', 'US', 'JP', 'PL', 'CL'];
    const selectedCountry = _.get(formData, PATH_COUNTRY);
    const hasAlphanumericPlz = alphanumericPlzCountries.includes(selectedCountry) || false;

    const isFirstRender = React.useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
        } else {
            let newData = _.cloneDeep(formData);
            _.set(newData, PATH_PLZ, undefined);

            dispatch(actionSetCIS4mDataObject(formId, newData));
        }
    }, [hasAlphanumericPlz]);

    return (
        <CIS4mBlock ref={mobileFormBlockRef} hidden={hidden}>
            <CIS4mTextField
                label={textMapCommon.streetAndNumber}
                path={PATH_STREET}
                required={isRequired(streetAndNumber)}
                hidden={!streetAndNumber.included}
                maxLength={streetAndNumber.maxLength}
                helperText={streetAndNumber.helperText}
                readonly={isReadonly(streetAndNumber)}
            />
            <CIS4mTextField
                label={textMapCommon.adresszusatz}
                path={PATH_ZUSATZ}
                required={isRequired(additionalInfo)}
                hidden={!additionalInfo.included}
                maxLength={additionalInfo.maxLength}
                helperText={additionalInfo.helperText}
                readonly={isReadonly(additionalInfo)}
            />
            {!hasAlphanumericPlz ? (
                <CIS4mNumberField
                    label={textMapCommon.plz}
                    path={PATH_PLZ}
                    maxLength={postalCode.maxLength}
                    required={isRequired(postalCode)}
                    hidden={!postalCode.included}
                    helperText={postalCode.helperText}
                    readonly={isReadonly(postalCode)}
                />
            ) : (
                <CIS4mTextField
                    label={textMapCommon.plz}
                    path={PATH_PLZ}
                    required={isRequired(postalCode)}
                    hidden={!postalCode.included}
                    maxLength={postalCode.maxLength}
                    helperText={postalCode.helperText}
                    readonly={isReadonly(postalCode)}
                />
            )}

            <CIS4mTextField
                label={textMapCommon.city}
                path={PATH_ORT}
                required={isRequired(location)}
                hidden={!location.included}
                maxLength={location.maxLength}
                helperText={location.helperText}
                readonly={isReadonly(location)}
            />
            <CountryField
                label={textMapCommon.country}
                path={PATH_COUNTRY}
                required={isRequired(country)}
                hidden={!country.included}
                readonly={isReadonly(country)}
            />
        </CIS4mBlock>
    );
});

AddressBlock.displayName = 'AddressBlock';
